/**
 * Mora de mensualidades.
 *
 * Reglas (contrato funcional §8), implementadas con aritmetica ENTERA para que
 * pantalla, recibo, Excel y base de datos den exactamente la misma cifra:
 *
 *   1. 1.5 % diario sobre el monto ORIGINAL de la mensualidad.
 *   2. Se cuenta por dias calendario desde el primer dia del mes siguiente al vencimiento.
 *   3. Sin limite maximo.
 *   4. No hay mora sobre mora.
 *   5. No se capitaliza.
 *   6. Un abono parcial NO reduce la base: la mora sigue sobre el monto original.
 *   7. Dentro de una mensualidad, todo pago cubre primero mora y luego capital.
 *   8. Cuando el capital queda saldado, deja de generarse mora.
 *   9. La exoneracion es total por mensualidad y no vuelve a generarse.
 *
 * El 1.5 % se expresa como 15 por mil (`ratePerMille`), no como 0.015: asi el
 * calculo es `original * 15 * dias / 1000` sobre enteros, sin punto flotante.
 */

import { type OperationalDate, isOperationalDate } from "./clock.js";
import { assertDomain } from "./errors.js";
import { type Money, money, subtract, zero } from "./money.js";

/** 1.5 % diario expresado por mil. Es el valor por defecto de la institucion. */
export const DEFAULT_LATE_FEE_RATE_PER_MILLE = 15;

/**
 * Estado de una mensualidad, tal como se reconstruye desde la base.
 * Es la entrada de TODOS los calculos de mora; no hay otra fuente.
 */
export interface MonthlyChargeState {
  /** Monto original del cargo, congelado al generarlo. Nunca cambia. */
  readonly originalAmount: Money;
  /**
   * Primer dia con mora: el 1 del mes siguiente al vencimiento.
   * `null` en conceptos que no generan mora (inscripcion, material, etc.).
   */
  readonly lateFeeStartDate: OperationalDate | null;
  /** Tasa por mil congelada en el cargo (15 = 1.5 % diario). */
  readonly lateFeeRatePerMille: number;
  /** Capital ya cubierto por pagos vigentes. */
  readonly capitalPaid: Money;
  /** Mora ya cubierta por pagos vigentes. */
  readonly lateFeePaid: Money;
  /**
   * Fecha operativa en que el capital quedo completamente saldado.
   * A partir de ese dia la mora deja de crecer (regla 8).
   */
  readonly capitalSettledOn: OperationalDate | null;
  /** `true` si un Administrador exonero la mora de esta mensualidad (regla 9). */
  readonly lateFeeWaived: boolean;
  /**
   * Mora ACORDADA con el encargado, cuando la hubo.
   *
   * `null` significa que no hay acuerdo y la mora corre normalmente. Un número es
   * la cantidad que se cobrará, y el resto queda condonado: «son Q150, quedamos en
   * Q100» se guarda como 10000.
   *
   * Un acuerdo CONGELA la mora. Si siguiera creciendo, los Q100 pactados hoy
   * serían Q105 la semana que viene y el acuerdo no valdría nada. Por eso una
   * exoneración total no es un caso aparte: es un acuerdo de cero.
   */
  readonly lateFeeAgreedMinor?: number | null;
  /** `true` si el cargo fue cancelado (por desactivacion del concepto, p. ej.). */
  readonly cancelled: boolean;
}

/**
 * Mora fijada por decisión, o `null` si sigue corriendo.
 *
 * Unifica los dos caminos: el acuerdo explícito y la exoneración total de siempre,
 * que es un acuerdo de cero. Todo lo demás del módulo pregunta por aquí, así que
 * no puede haber una función que respete el acuerdo y otra que lo ignore.
 */
function moraFijada(state: MonthlyChargeState): number | null {
  if (
    state.lateFeeAgreedMinor !== undefined &&
    state.lateFeeAgreedMinor !== null
  ) {
    return state.lateFeeAgreedMinor;
  }
  return state.lateFeeWaived ? 0 : null;
}

/** Milisegundos de un dia. Las fechas operativas se comparan en UTC puro. */
const DAY_MS = 86_400_000;

function toUtcMs(date: OperationalDate): number {
  return Date.parse(`${date}T00:00:00.000Z`);
}

/**
 * Dias calendario transcurridos entre dos fechas operativas, ambos extremos incluidos.
 * Al ser fechas puras (sin hora), el calculo es exacto y no lo afecta el horario de verano.
 */
export function inclusiveDayCount(
  from: OperationalDate,
  to: OperationalDate,
): number {
  const days = Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS) + 1;
  return days > 0 ? days : 0;
}

/**
 * Dias de mora acumulados a la fecha indicada.
 *
 * El conteo es INCLUSIVO desde `lateFeeStartDate`: el 1 de febrero, la mensualidad
 * de enero ya lleva 1 dia de mora (el contrato lo llama "primer dia con mora").
 * Si el capital ya se salda, el conteo se congela en la fecha de saldo (regla 8).
 */
export function lateFeeDays(
  state: MonthlyChargeState,
  asOf: OperationalDate,
): number {
  // Con acuerdo no se cuentan días: el importe ya está fijado y no crece.
  if (state.cancelled || moraFijada(state) !== null) return 0;
  if (state.lateFeeStartDate === null) return 0;
  assertDomain(
    isOperationalDate(asOf),
    "NUMERACION_DIA_INVALIDO",
    "Fecha operativa inválida",
    {
      asOf,
    },
  );

  // El capital saldado congela la mora en el dia en que se salda.
  const cutoff =
    state.capitalSettledOn !== null && state.capitalSettledOn < asOf
      ? state.capitalSettledOn
      : asOf;
  if (cutoff < state.lateFeeStartDate) return 0;
  return inclusiveDayCount(state.lateFeeStartDate, cutoff);
}

/**
 * Division entera con redondeo half-up. `numerator` y `divisor` son enteros
 * positivos; el resultado es el centavo mas cercano, con .5 hacia arriba.
 */
function divideHalfUp(numerator: number, divisor: number): number {
  assertDomain(divisor > 0, "MONTO_INVALIDO", "Divisor inválido", { divisor });
  const negative = numerator < 0;
  const value = Math.abs(numerator);
  const rounded = Math.floor((value + Math.floor(divisor / 2)) / divisor);
  return negative ? -rounded : rounded;
}

/**
 * Mora ACUMULADA (bruta) de la mensualidad a la fecha indicada.
 *
 * `original * tasaPorMil * dias / 1000`, en enteros, redondeando una sola vez al
 * final. Redondear a diario acumularia centavos de error a lo largo de meses.
 */
export function accruedLateFee(
  state: MonthlyChargeState,
  asOf: OperationalDate,
): Money {
  const currency = state.originalAmount.currency;
  if (state.cancelled) return zero(currency);
  const fijada = moraFijada(state);
  if (fijada !== null) return money(fijada, currency);

  const days = lateFeeDays(state, asOf);
  if (days === 0) return zero(currency);

  assertDomain(
    Number.isSafeInteger(state.lateFeeRatePerMille) &&
      state.lateFeeRatePerMille >= 0,
    "MONTO_INVALIDO",
    "Tasa de mora inválida",
    { rate: state.lateFeeRatePerMille },
  );

  const numerator =
    state.originalAmount.minor * state.lateFeeRatePerMille * days;
  assertDomain(
    Number.isSafeInteger(numerator),
    "MONTO_INVALIDO",
    "La mora acumulada excede el rango representable",
    { days },
  );
  return money(divideHalfUp(numerator, 1000), currency);
}

/** Capital que aun se debe de la mensualidad. Nunca negativo. */
export function outstandingCapital(state: MonthlyChargeState): Money {
  if (state.cancelled) return zero(state.originalAmount.currency);
  const remaining = subtract(state.originalAmount, state.capitalPaid);
  return remaining.minor > 0 ? remaining : zero(state.originalAmount.currency);
}

/** Mora que aun se debe: la acumulada menos la ya pagada. Cero si fue exonerada. */
export function outstandingLateFee(
  state: MonthlyChargeState,
  asOf: OperationalDate,
): Money {
  const accrued = accruedLateFee(state, asOf);
  const remaining = subtract(accrued, state.lateFeePaid);
  return remaining.minor > 0 ? remaining : zero(state.originalAmount.currency);
}

/** Deuda total de la mensualidad: mora pendiente + capital pendiente. */
export function totalOutstanding(
  state: MonthlyChargeState,
  asOf: OperationalDate,
): Money {
  const lateFee = outstandingLateFee(state, asOf);
  const capital = outstandingCapital(state);
  return money(lateFee.minor + capital.minor, state.originalAmount.currency);
}

/** `true` si la mensualidad esta completamente saldada (capital y mora). */
export function isSettled(
  state: MonthlyChargeState,
  asOf: OperationalDate,
): boolean {
  return totalOutstanding(state, asOf).minor === 0;
}

// ---------------------------------------------------------------------------
// Aplicacion de un pago dentro de una mensualidad
// ---------------------------------------------------------------------------

export interface MonthlyApplication {
  /** Parte del importe que cubre mora. */
  readonly toLateFee: Money;
  /** Parte del importe que cubre capital. */
  readonly toCapital: Money;
  /** Sobrante que no cupo en esta mensualidad. */
  readonly remainder: Money;
  /** `true` si con esta aplicacion el capital queda completamente saldado. */
  readonly settlesCapital: boolean;
}

/**
 * Reparte un importe dentro de UNA mensualidad: primero mora, despues capital (regla 7).
 *
 * Es pura y determinista: la usan la PWA para mostrar el desglose antes de confirmar
 * y la API para persistir las lineas. No pueden divergir porque es la misma funcion.
 */
export function applyToMonthlyCharge(
  state: MonthlyChargeState,
  amount: Money,
  asOf: OperationalDate,
): MonthlyApplication {
  const currency = state.originalAmount.currency;
  assertDomain(
    amount.currency === currency,
    "MONEDA_INCONSISTENTE",
    "La moneda del pago no coincide con la del cargo",
    { expected: currency, received: amount.currency },
  );
  assertDomain(
    amount.minor >= 0,
    "MONTO_NO_POSITIVO",
    "El importe no puede ser negativo",
    {
      amount: amount.minor,
    },
  );
  assertDomain(
    !state.cancelled,
    "CARGO_NO_ENCONTRADO",
    "El cargo fue cancelado",
    {},
  );

  let remaining = amount.minor;

  const lateFeeDue = outstandingLateFee(state, asOf).minor;
  const toLateFee = Math.min(remaining, lateFeeDue);
  remaining -= toLateFee;

  const capitalDue = outstandingCapital(state).minor;
  const toCapital = Math.min(remaining, capitalDue);
  remaining -= toCapital;

  return {
    toLateFee: money(toLateFee, currency),
    toCapital: money(toCapital, currency),
    remainder: money(remaining, currency),
    settlesCapital: capitalDue > 0 && toCapital === capitalDue,
  };
}

// ---------------------------------------------------------------------------
// Exoneracion de mora
// ---------------------------------------------------------------------------

export interface WaiverCandidate {
  readonly chargeId: string;
  readonly month: number;
  readonly cycleId: string;
  readonly state: MonthlyChargeState;
  /**
   * Mora que se ACUERDA cobrar. Ausente o cero es la exoneración de siempre.
   *
   * Existe porque en la ventanilla se negocia: «son Q150 de mora, pero quedamos en
   * que paga Q100». Antes eso obligaba a exonerar los Q150 completos y cobrar los
   * Q100 como si fueran otra cosa, con lo que el reporte de mora exonerada mentía.
   */
  readonly agreedMinor?: number;
}

export interface WaiverLine {
  readonly chargeId: string;
  readonly month: number;
  readonly cycleId: string;
  /** Mora que se exonera. Solo mora: el capital NO se toca (regla 15). */
  readonly waivedAmount: Money;
  /** Mora que se seguirá cobrando tras el acuerdo. Cero en una exoneración total. */
  readonly agreedAmount: Money;
}

/**
 * Solo son elegibles las mensualidades con mora acumulada mayor que cero, no
 * canceladas, no exoneradas ya, y cuyo dia de mora ya llego (regla 13 y 14).
 */
export function isWaiverEligible(
  state: MonthlyChargeState,
  asOf: OperationalDate,
): boolean {
  // Un mes con acuerdo ya cerrado no vuelve a negociarse en silencio: su importe
  // está fijado y renegociarlo tiene que ser una decisión visible, no un efecto
  // colateral de volver a marcarlo.
  if (state.cancelled || moraFijada(state) !== null) return false;
  if (state.lateFeeStartDate === null) return false;
  return outstandingLateFee(state, asOf).minor > 0;
}

/**
 * Arma la vista previa de una exoneracion de varios meses del MISMO alumno.
 *
 * Regla 18: si alguna mensualidad seleccionada no es elegible, se cancela la
 * operacion COMPLETA. Nunca se aplica una exoneracion parcial en silencio.
 */
export function buildWaiverPreview(
  candidates: readonly WaiverCandidate[],
  asOf: OperationalDate,
  currency: string,
): { lines: WaiverLine[]; total: Money } {
  assertDomain(
    candidates.length > 0,
    "PAGO_SIN_DISTRIBUCION",
    "Debe seleccionar al menos una mensualidad con mora",
    {},
  );

  const lines: WaiverLine[] = [];
  for (const candidate of candidates) {
    assertDomain(
      isWaiverEligible(candidate.state, asOf),
      "CONFLICTO_REQUIERE_REVISION",
      `La mensualidad seleccionada ya no tiene mora exonerable. Vuelva a calcular la vista previa.`,
      { chargeId: candidate.chargeId, month: candidate.month },
    );
    const pendiente = outstandingLateFee(candidate.state, asOf);
    const acordado = candidate.agreedMinor ?? 0;

    assertDomain(
      Number.isSafeInteger(acordado) && acordado >= 0,
      "MONTO_INVALIDO",
      "La mora acordada debe ser un importe válido",
      { chargeId: candidate.chargeId, acordado },
    );
    // Acordar MÁS de lo que se debe no es un acuerdo, es inventar deuda. Y dejaría
    // un mes con una mora superior a la que el propio calendario venía mostrando.
    assertDomain(
      acordado <= pendiente.minor,
      "MONTO_INVALIDO",
      "La mora acordada no puede superar la mora pendiente",
      { chargeId: candidate.chargeId, acordado, pendiente: pendiente.minor },
    );

    lines.push({
      chargeId: candidate.chargeId,
      month: candidate.month,
      cycleId: candidate.cycleId,
      waivedAmount: money(pendiente.minor - acordado, currency),
      agreedAmount: money(acordado, currency),
    });
  }

  const total = lines.reduce((acc, line) => acc + line.waivedAmount.minor, 0);
  return {
    lines: lines.sort((a, b) =>
      a.cycleId === b.cycleId
        ? a.month - b.month
        : a.cycleId.localeCompare(b.cycleId),
    ),
    total: money(total, currency),
  };
}

/**
 * Estado resultante tras el acuerdo.
 *
 * La mora queda FIJADA en lo acordado y no vuelve a generarse aunque siga habiendo
 * capital pendiente (regla 19). Con `agreedMinor` en cero es la exoneración total
 * de siempre; con un importe, ese importe es lo único que quedará por cobrar.
 */
export function applyWaiver(
  state: MonthlyChargeState,
  agreedMinor = 0,
): MonthlyChargeState {
  return {
    ...state,
    lateFeeWaived: agreedMinor === 0,
    lateFeeAgreedMinor: agreedMinor,
  };
}
